import { useEffect, useRef, useCallback, useState } from 'react';
import { tokenUtils } from '../utils/api';
import { logregApi } from '../api/logreg';

/**
 * Session Manager Hook
 *
 * Handles:
 * 1. Proactive token refresh (refresh 2 minutes before expiry)
 * 2. Idle activity detection (30 minutes of inactivity)
 * 3. Warning modal (60 seconds before auto-logout)
 * 4. Automatic logout on timeout
 *
 * Security features:
 * - Tokens are refreshed proactively to prevent service interruption
 * - Idle sessions are terminated to prevent unauthorized access
 * - Warning gives users a chance to stay logged in
 * - All timing based on JWT expiry for accuracy
 */

// Constants
const IDLE_WARNING_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
const LOGOUT_COUNTDOWN = 60 * 1000; // 60 seconds countdown after warning
const TOKEN_REFRESH_BUFFER = 2 * 60 * 1000; // Refresh 2 minutes before expiry
const MIN_TOKEN_LIFETIME = 5 * 60 * 1000; // Minimum 5 minutes before we try to refresh

// Activity events to monitor
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click'
];

export const useSessionManager = (isAuthenticated, onLogout) => {
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);

  // Refs to prevent stale closures
  const lastActivityTime = useRef(Date.now());
  const idleWarningTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownInterval = useRef(null);
  const tokenRefreshTimer = useRef(null);
  const isRefreshing = useRef(false);
  const showIdleWarningRef = useRef(false);

  /**
   * Decode JWT token to get expiry time
   */
  const getTokenExpiry = useCallback(() => {
    try {
      const { token } = tokenUtils.getTokens();
      if (!token) return null;

      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      return null;
    }
  }, []);

  /**
   * Proactively refresh token before it expires
   */
  const refreshToken = useCallback(async () => {
    if (isRefreshing.current) {
      return false;
    }

    isRefreshing.current = true;

    try {
      const { token, refreshKey } = tokenUtils.getTokens();

      if (!token || !refreshKey) {
        isRefreshing.current = false;
        return false;
      }

      const refreshResult = await logregApi.refreshSessionToken(refreshKey);

      if (refreshResult.success && refreshResult.data) {
        const newToken = refreshResult.data.sessionToken || refreshResult.data.token;
        const newRefreshKey = refreshResult.data.refreshKey || refreshKey;

        if (!newToken) {
          isRefreshing.current = false;
          return false;
        }

        // Store new tokens (preserve keepSignedIn preference)
        const keepSignedIn = localStorage.getItem('sessionToken') !== null;
        tokenUtils.storeTokens(newToken, newRefreshKey, keepSignedIn);

        // Schedule next refresh based on new token expiry
        scheduleTokenRefresh();

        isRefreshing.current = false;
        return true;
      } else {
        isRefreshing.current = false;
        return false;
      }
    } catch (error) {
      isRefreshing.current = false;
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- scheduleTokenRefresh and refreshToken are mutually recursive; adding dep would cause infinite loop
  }, []);

  /**
   * Schedule next token refresh based on token expiry
   */
  const scheduleTokenRefresh = useCallback(() => {
    // Clear existing timer
    if (tokenRefreshTimer.current) {
      clearTimeout(tokenRefreshTimer.current);
      tokenRefreshTimer.current = null;
    }

    const expiryTime = getTokenExpiry();
    if (!expiryTime) {
      return;
    }

    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;
    const timeUntilRefresh = timeUntilExpiry - TOKEN_REFRESH_BUFFER;

    if (timeUntilRefresh < MIN_TOKEN_LIFETIME) {
      // Token expires very soon or already expired, refresh immediately
      refreshToken();
    } else {
      // Schedule refresh for 2 minutes before expiry
      tokenRefreshTimer.current = setTimeout(() => {
        refreshToken();
      }, timeUntilRefresh);
    }
  }, [getTokenExpiry, refreshToken]);

  /**
   * Handle idle logout
   */
  const handleIdleLogout = useCallback(() => {
    // Clean up all timers
    if (idleWarningTimer.current) clearTimeout(idleWarningTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    if (tokenRefreshTimer.current) clearTimeout(tokenRefreshTimer.current);

    showIdleWarningRef.current = false;
    setShowIdleWarning(false);

    // Call the logout function
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  /**
   * Record user activity and reset idle timers
   */
  const recordActivity = useCallback(() => {
    const now = Date.now();
    lastActivityTime.current = now;

    // Don't restart timer if warning is already showing
    if (showIdleWarningRef.current) return;

    // Reset idle warning timer
    if (idleWarningTimer.current) {
      clearTimeout(idleWarningTimer.current);
    }

    // Schedule idle warning
    idleWarningTimer.current = setTimeout(() => {
      showIdleWarningRef.current = true;
      setShowIdleWarning(true);
      setCountdown(60);

      // Start countdown using functional state updates (more reliable)
      countdownInterval.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Schedule automatic logout after 60 seconds
      logoutTimer.current = setTimeout(() => {
        handleIdleLogout();
      }, LOGOUT_COUNTDOWN);
    }, IDLE_WARNING_TIMEOUT);
  }, [handleIdleLogout]);

  /**
   * User chooses to stay logged in
   */
  const stayLoggedIn = useCallback(() => {
    // Clear all timers
    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }

    // Hide warning and reset activity
    showIdleWarningRef.current = false;
    setShowIdleWarning(false);
    recordActivity();
  }, [recordActivity]);

  /**
   * Setup activity listeners
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Schedule token refresh
    scheduleTokenRefresh();

    // Setup activity listeners
    const throttledRecordActivity = throttle(recordActivity, 1000); // Max once per second

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, throttledRecordActivity, { passive: true });
    });

    // Record initial activity
    recordActivity();

    // Cleanup - only clear activity listeners and idle warning timer
    // Don't clear countdown/logout timers here as they need to persist
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, throttledRecordActivity);
      });

      if (idleWarningTimer.current) clearTimeout(idleWarningTimer.current);
      if (tokenRefreshTimer.current) clearTimeout(tokenRefreshTimer.current);
    };
  }, [isAuthenticated, recordActivity, scheduleTokenRefresh]);

  /**
   * Cleanup countdown and logout timers on unmount only
   */
  useEffect(() => {
    return () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, []);

  return {
    showIdleWarning,
    countdown,
    stayLoggedIn,
    handleIdleLogout
  };
};

/**
 * Throttle function to limit execution rate
 */
function throttle(func, delay) {
  let lastCall = 0;
  return function(...args) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
}

export default useSessionManager;
